import { api, definirAoExpirarInspecao, definirAoPerderSessao, entrar, falhouPorRede, largarTokenDeInspecao, renovarSessao, sair, usarTokenDeInspecao } from './api';
import { adminReal, camada, diasAtras, modoOffline, municipioSel, pesquisaId, secretariaId, sessaoPronta, usuario } from './state';
import type { Usuario } from './types';

// Só o pesquisador trabalha sem internet. Guardamos quem foi o último pesquisador deste aparelho para, sem rede,
// abrir o app de coleta e a fila dele. Isso NÃO dá acesso à API: ela continua exigindo o login de verdade.
const CHAVE_ULTIMO = 'mapas.coleta.ultimoUsuario';
function lembrar(u: Usuario | null) {
  try {
    // Só quem já trocou a senha temporária: senão o app abriria offline pedindo uma troca que não consegue concluir.
    if (u?.perfil === 'PESQUISADOR' && !u.deveTrocarSenha) localStorage.setItem(CHAVE_ULTIMO, JSON.stringify(u));
    else localStorage.removeItem(CHAVE_ULTIMO);
  } catch { /* armazenamento indisponível: segue sem modo offline */ }
}
function lembrado(): Usuario | null {
  try {
    const u = JSON.parse(localStorage.getItem(CHAVE_ULTIMO) ?? 'null') as Usuario | null;
    return u?.perfil === 'PESQUISADOR' ? u : null;
  } catch {
    return null;
  }
}

function limparFiltros() {
  camada.value = 'progresso';
  diasAtras.value = 0;
  secretariaId.value = '';
  pesquisaId.value = '';
  municipioSel.value = null;
}

definirAoPerderSessao(() => {
  usuario.value = null;
});

/** Chamada uma vez ao abrir o app: se o cookie de refresh ainda vale, entra sem pedir senha. */
export async function iniciarSessao() {
  const s = await renovarSessao();
  if (s) lembrar(s.usuario);
  const offline = !s && falhouPorRede() ? lembrado() : null;
  modoOffline.value = offline !== null;
  usuario.value = s?.usuario ?? offline;
  sessaoPronta.value = true;
}

export async function fazerLogin(email: string, senha: string) {
  usuario.value = await entrar(email, senha);
  modoOffline.value = false;
  lembrar(usuario.value);
}

/** Voltou a conexão no modo offline: tenta renovar a sessão de verdade; se ela acabou, volta para o login (a fila local fica guardada). */
export async function reconectar(): Promise<boolean> {
  const s = await renovarSessao();
  if (s) {
    usuario.value = s.usuario;
    modoOffline.value = false;
    lembrar(s.usuario);
    return true;
  }
  if (!falhouPorRede()) {
    modoOffline.value = false;
    usuario.value = null;
  }
  return false;
}

// ---------------------------------------------------------------- inspeção ("ver como")

let encerrando = false;

/**
 * Passa a ver o sistema como outra pessoa vê (só leitura; a API garante). NÃO grava nada no aparelho (nem "último pesquisador"):
 * a sessão do administrador continua guardada à parte e volta quando a inspeção termina.
 */
export async function inspecionar(id: string) {
  const r = await api<{ accessToken: string; usuario: Usuario }>(`/auth/inspecionar/${id}`, { method: 'POST' });
  adminReal.value = usuario.value;
  usarTokenDeInspecao(r.accessToken);
  limparFiltros();
  usuario.value = r.usuario;
  location.hash = '#/painel';
}

/** Volta para a conta do administrador. `avisarApi=false` quando a inspeção já acabou por tempo (a API recusaria o aviso). */
export async function encerrarInspecao(avisarApi = true) {
  if (!adminReal.value || encerrando) return;
  encerrando = true;
  try {
    if (avisarApi) await api('/auth/inspecao/encerrar', { method: 'POST' }).catch(() => {});
    largarTokenDeInspecao();
    limparFiltros();
    const s = await renovarSessao(); // o cookie de renovação do administrador nunca foi tocado
    adminReal.value = null;
    usuario.value = s?.usuario ?? null;
    location.hash = '#/usuarios';
  } finally {
    encerrando = false;
  }
}

definirAoExpirarInspecao(() => void encerrarInspecao(false));

export async function fazerLogout() {
  // Sair no meio de uma inspeção sai de verdade da conta do administrador (e não deixa a sessão dele aberta).
  if (adminReal.value) await encerrarInspecao();
  try {
    await sair();
  } finally {
    lembrar(null);
    modoOffline.value = false;
    usuario.value = null;
    limparFiltros(); // outra pessoa pode entrar neste navegador: nada da sessão anterior fica na tela
  }
}

/** Depois de trocar a senha, a API deixa de exigir troca; reflete isso na tela. */
export function senhaTrocada() {
  if (usuario.value) {
    usuario.value = { ...usuario.value, deveTrocarSenha: false };
    lembrar(usuario.value);
  }
}
